import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Col} from "reactstrap";
import {
    DataGridActionTypes,
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridExoticColumnFields,
    useDataGridSavedState
} from "../../../../../../../../../../packages/data-grid";
import {FreeShippingFeesManagementCompaniesDataGridColumnNames} from "../../../../../../../../../../core/constants/table/data-grid-column-keys";
import useIsMounted from "../../../../../../../../../hooks/use-is-mounted";
import PartnerDataGrid from "../../../../../../../../../containers/data-grid";
import Api from "../../../../../../../../../../core/services/api/api";
import {FreeShippingFeesManagementViewDataGridActionNames} from "../../index";
import {OutlinedInput} from "../../../../../../../../../components/base/input/outlined_input";
import IconButton from "../../../../../../../../../components/base/button/icon_button";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import {formatMoney} from "../../../../../../../../../../core/services/utils/utils";
import {ReactComponent as RemoveIcon} from "../../../../../../../../../../assets/images/remove.svg";
import {OutlinedDropdown} from "../../../../../../../../../components/base/dropdown/outlined_dropdown";
import Button from "../../../../../../../../../components/base/button/button";


const dataGridSaveConfig = {
    name: 'FreeShippingFeesManagementViewSettingsCompaniesExceptions',
    version: 1,
}

const FreeShippingFeesManagementViewSettingsCompaniesExceptions = ({
                                                                       customerId,
                                                                       customerName,
                                                                       syncWithDetachedCustomers,
                                                                       data: dataProp,
                                                                       refresher,
                                                                   }) => {
    const [data, setData] = useState([]);
    const [allCompanies, setAllCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState('');
    const isMounted = useIsMounted();
    /** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridController = useRef();

    const isStaticData = useMemo(() => !!dataProp?.length, [dataProp?.length])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: false,
                },
                {
                    title: "Company",
                    name: FreeShippingFeesManagementCompaniesDataGridColumnNames.company,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                {
                    title: "Threshold",
                    name: FreeShippingFeesManagementCompaniesDataGridColumnNames.threshold,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
            ],
        [])

    // eslint-disable-next-line no-unused-vars
    const [dataGridColumns, _, __, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Searches through the entries of the free-shipping-fees management view from the server
     * @type {(function(*=, boolean=): Promise<*|[]>)|*}
     */
    const search = useCallback(async () => {
        if (isStaticData) {
            setData(dataProp);
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await Api.getFreightCompanyExceptions({
            customerOriginalId: customerId ?? null
        });
        if (!isMounted())
            return
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data ?? []);
        }
    }, [isMounted, customerId]);

    /**
     * Fetches all freight companies of the system.
     * @type {(function(): Promise<void>)|*}
     */
    const getAllCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setAllCompanies(response.data ?? []);
        }
    }, [isMounted])

    /**
     * With each change in the [search] callback:
     * - searches through the entries of the free-shipping-fees management view from the server
     */
    useEffect(() => {
        search().then();
        getAllCompanies().then();
    }, [search, getAllCompanies, refresher])


    /**
     * Adds a new entity to the list of records for this free-shipping-management view
     */
    const add = useCallback(async (entry) => {
        setAdding(true);
        const forApi = {
            freightCompanyId: entry.id,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
            threshold: null,
        }
        const response = await Api.saveFreightCompanyException(forApi);
        if (!isMounted())
            return;
        setAdding(false);
        if (response?.resultFlag) {
            setData(prevState => [{...response.data, threshold: null}, ...prevState]);
            setSelectedEntity('');
        }
    }, [customerId, customerName, syncWithDetachedCustomers, isMounted])

    /**
     * Calls the api regarding removing the list of data
     * @param ids list of ids associated to entries to be removed
     * @private
     */
    const remove = useCallback(async (companyIds) => {
        setLoading(true);
        const forApi = {
            freightCompanylIds: companyIds,
            customerOriginalId: customerId,
            applyToAll: syncWithDetachedCustomers,
        }
        const response = await Api.removeFreightCompanyException(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => !companyIds.includes(e.freightCompanyId)))
        }
        dataGridController.current?.resetSelection();
        setLoading(false);
    }, [isMounted, customerId, syncWithDetachedCustomers])

    /**
     * Updates the threshold of the entity for the fee-shipping-fees management view.
     * @type {function(*, null=): Promise<*>}
     */
    const edit = useCallback(async (entry, threshold = null) => {
        const forApi = {
            freightCompanyId: entry.freightCompanyId,
            customerOriginalId: customerId,
            customerTitle: customerName,
            applyToAll: syncWithDetachedCustomers,
            threshold: threshold,
        }
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: true} : e))
        const response = await Api.saveFreightCompanyException(forApi);
        if (!isMounted())
            return;
        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, editing: false} : e))
        if (response?.resultFlag) {
            setData(prevState => {
                const newState = Array.from(prevState)
                newState.splice(
                    prevState.findIndex(e => e.id === entry.id),
                    1,
                    {...response.data, threshold: forApi.threshold}
                );
                return newState;
            })
        }
        return response?.resultFlag ?? false;
    }, [customerId, customerName, syncWithDetachedCustomers, isMounted])


    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     * @type {function(Array<any>)}
     */
    const getRemoveConfirmation = useCallback((itemsToBeRemoved) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected item${itemsToBeRemoved?.length > 1 ? '(s)' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && remove(itemsToBeRemoved),
        );
    }, [remove])

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case FreeShippingFeesManagementViewDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.freightCompanyId));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.freightCompanyId));
                }
                getRemoveConfirmation(data.map(e => e.freightCompanyId));
                break;
            }
            default:
                break;
        }
    }, [data, getRemoveConfirmation])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     *
     * @param prevValue
     * @param newValue
     * @param {number | string} rowKey
     * @param {number} colName
     * @return {Promise<undefined|boolean>}
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        switch (colName) {
            case FreeShippingFeesManagementCompaniesDataGridColumnNames.threshold:
                let threshold = Number(newValue)
                if (Number.isNaN(threshold))
                    threshold = null;
                if (threshold === 0)
                    threshold = null;
                return edit(data?.find(e => e.id === rowKey), threshold);
            default:
                return;
        }
    }, [data, edit])

    /**@type {DataGridRow[]}}*/
    const dataGridRows = useMemo(() => {
        return data.map(entry => {
            const thresholdRemovable = entry?.threshold !== null;
            return ({
                key: entry.id,
                data: entry,
                cells: {
                    [DataGridExoticColumnFields.selection]: null,
                    [FreeShippingFeesManagementCompaniesDataGridColumnNames.company]: entry?.freightCompanyTitle,
                    [FreeShippingFeesManagementCompaniesDataGridColumnNames.threshold]: {
                        value: ({openEditPopover}) =>
                            <>
                                <Fade in={thresholdRemovable}>
                                        <span>
                                        <IconButton
                                            onPressed={() => edit(entry)}
                                            disabled={entry?.editing || !thresholdRemovable}
                                            color={'paper.error'}
                                        >
                                            {
                                                entry?.editing
                                                    ? <CircularProgress size={20} color={'secondary'}/>
                                                    : <SvgIcon component={RemoveIcon} className='icon small'/>
                                            }
                                        </IconButton>
                                        </span>
                                </Fade>
                                <IconButton
                                    className={classnames({'ml-2': thresholdRemovable})}
                                    onPressed={() => openEditPopover()}
                                    disabled={entry?.editing}
                                >
                                    {
                                        entry?.editing
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                                <p {...dataGridCellTextProps} className={classnames('ml-2')}>
                                    {entry.threshold === null ? 'Not Eligible at all' : formatMoney(entry.threshold)}
                                </p>
                            </>,
                        editable: true,
                        editableValue: entry.threshold,
                        EditableContentComponent: <ThresholdEditor/>,
                    },
                },
            })
        })
    }, [data, edit])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: FreeShippingFeesManagementViewDataGridActionNames.Delete,
                title: 'Remove',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    const dropdownData = useMemo(() => {
        const existingCompanyIds = data.map(e => e.freightCompanyId);
        return allCompanies
            .filter(e => !existingCompanyIds.includes(e.id))
            .map(e => ({
                id: e.id,
                label: e.title,
                value: e,
            }))
    }, [allCompanies, data])

    return (
        <>
            <div className={'section'}>
                <p className={'section-title'}>
                    Freight Companies
                </p>
                <p className={'section-description'}>
                    Orders shipping with the following freight companies are not eligible for free-shipping. The freight
                    companies with a
                    threshold, shall also be eligible for free shipping if the order total is more than the provided
                    threshold value.
                </p>
                <div className={'section-paper'}>
                    <div className={'d-flex flex-wrap w-100'}>
                        {
                            !isStaticData &&
                            <>
                                <Col xs={9}>
                                    <OutlinedDropdown
                                        label={'New Company'}
                                        onChange={(entity) => setSelectedEntity(entity)}
                                        value={selectedEntity}
                                        data={dropdownData}
                                        disabled={adding}
                                    />
                                </Col>
                                <Col xs={3}>
                                    <Button
                                        type={"button"}
                                        variant={"contained"}
                                        className={'py-2-5 w-100'}
                                        color={'primary'}
                                        disabled={adding || !selectedEntity}
                                        onClick={() => add(selectedEntity)}
                                    >
                                        {
                                            adding ? "Adding..." : "Add"
                                        }
                                    </Button>
                                </Col>
                            </>
                        }
                        <PartnerDataGrid
                            ref={dataGridController}
                            {...dataGridSaveConfig}
                            visibleRows={5}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            hideToolbar
                            hidePagination
                            hidePageSize
                            hideSelectAll
                            columns={dataGridColumns}
                            rows={dataGridRows}
                            footerActions={footerActions}
                            onActionInvoked={onActionInvoked}
                            onCellEdited={onCellEdited}
                            hideFooter={isStaticData}
                            state={{
                                loading: {state: loading},
                                density: dataGridDensity,
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

const ThresholdEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [value])

    /**
     * Changes the value of this editor.
     *
     * - ensures the value is non-negative.
     * @param value
     */
    const onValueChanged = (value) => {
        const parsed = Number(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(Math.max(0, parsed));
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                label={"Threshold"}
                size={'small'}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                }}
            />
        </>
    );
}

export default FreeShippingFeesManagementViewSettingsCompaniesExceptions;
